import { Translate } from '@chipinside/translate'
import { Box, Drawer as MuiDrawer, IconButton, Tooltip } from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { DrawerDefaultHeader } from '#/components/Drawer/DefaultHeader'
import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerWrapper,
} from '#/components/Drawer/styles'
import type {
  DrawerComponentProps,
  DrawerContainerProps,
  DrawerOptions,
} from '#/components/Drawer/types'
import { Icon } from '#/components/Icon'

export { DrawerDefaultHeader }

/** Propriedades padrão para o Drawer */
export const defaultDrawerOptions: DrawerOptions = {
  anchor: 'right',
  open: false,
  width: 500,
}

/** Controlador do Drawer, usado para abrir, fechar e atualizar as opções dinamicamente */
export class DrawerController {
  private updateDrawerOptions: Dispatch<SetStateAction<DrawerOptions>> | null = null

  /** Inicializa o controlador com o setter de estado */
  initialize = (updateFn: Dispatch<SetStateAction<DrawerOptions>>) => {
    this.updateDrawerOptions = updateFn
  }

  /** Aguarda o updateDrawerOptions ser inicializado */
  waitForInitialize = async () => {
    if (this.updateDrawerOptions) return true

    return new Promise(resolve => {
      const check = () => {
        if (this.updateDrawerOptions) {
          resolve(true)
          clearInterval(interval)
        }
      }
      const interval = setInterval(check, 100)
    })
  }

  /** Abre o Drawer com as opções fornecidas */
  open = (options: Omit<DrawerOptions, 'open'>) => {
    this.options({ ...options, open: true })
  }

  /** Fecha o Drawer */
  close = () => this.options(defaultDrawerOptions)

  /** Atualiza as opções do Drawer sem necessariamente abrir ou fechar */
  options = async (options: DrawerOptions) => {
    await this.waitForInitialize()
    this.updateDrawerOptions?.({ ...defaultDrawerOptions, ...options })
  }

  /** Atualiza parcialmente as opções do Drawer, preservando as demais sem desmontar o content */
  update = async (options: Partial<DrawerOptions>) => {
    await this.waitForInitialize()
    this.updateDrawerOptions?.(previous => ({ ...previous, ...options }))
  }
}

/** Drawer controlado pelas opções recebidas, sem depender do controlador global */
export const DrawerComponent = ({ onClose, options }: DrawerComponentProps) => {
  return (
    <MuiDrawer
      anchor={options.anchor}
      keepMounted={options.keepMounted}
      open={options.open}
      onClose={onClose}
      className="drawer"
      sx={{ zIndex: options.zIndex }}
    >
      <DrawerWrapper width={options.width}>
        <DrawerHeader sx={options.header?.sx}>
          <Box sx={{ flex: 1 }}>
            <DrawerDefaultHeader {...(options.header ?? {})} />
          </Box>

          <Tooltip title={Translate({ messageKey: 'close' })}>
            <IconButton onClick={onClose}>
              <Icon size="large" name="mui-close" />
            </IconButton>
          </Tooltip>
        </DrawerHeader>

        <DrawerContent sx={options.contentSx}>{options.content}</DrawerContent>

        {options.footer && <DrawerFooter>{options.footer}</DrawerFooter>}
      </DrawerWrapper>
    </MuiDrawer>
  )
}

/** Drawer que renderiza o conteúdo conforme as opções recebidas pelo controlador, o global por padrão */
export const DrawerContainer = ({ controller = Drawer }: DrawerContainerProps) => {
  const [options, setOptions] = useState<DrawerOptions>(defaultDrawerOptions)

  useEffect(() => {
    controller.initialize(setOptions)
  }, [controller])

  return <DrawerComponent options={options} onClose={options.onClose ?? controller.close} />
}

/** Instância global do controlador do Drawer */
export const Drawer = new DrawerController()
